/**
 * Dictionary Page - Optimized with UI/UX Pro Max guidelines
 * Features: Autocomplete, Search History, Skeleton Loading, Accessibility
 */

import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ClickableText } from '../components/ClickableWord';
import { formatPOS as formatPOSUtil, getCurrentLocale, SupportedLocale } from '../utils/posLabels';
import './DictionaryPage.css';

import { API_BASE_URL } from '../config';
const RECENT_SEARCHES_KEY = 'dictionary_recent_searches';
const MAX_RECENT_SEARCHES = 8;

// SVG Icons
const Icons = {
  Book: () => (
    <svg className="header-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
      <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
    </svg>
  ),
  Search: ({ className }: { className?: string }) => (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="11" cy="11" r="8" />
      <path d="m21 21-4.35-4.35" />
    </svg>
  ),
  X: ({ className }: { className?: string }) => (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  ),
  Clock: ({ className }: { className?: string }) => (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10" />
      <polyline points="12 6 12 12 16 14" />
    </svg>
  ),
  Lightbulb: ({ className }: { className?: string }) => (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M9 18h6" />
      <path d="M10 22h4" />
      <path d="M15.09 14c.18-.98.65-1.74 1.41-2.5A4.65 4.65 0 0 0 18 8 6 6 0 0 0 6 8c0 1 .23 2.23 1.5 3.5A4.61 4.61 0 0 1 8.91 14" />
    </svg>
  ),
  AlertCircle: ({ className }: { className?: string }) => (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10" />
      <line x1="12" y1="8" x2="12" y2="12" />
      <line x1="12" y1="16" x2="12.01" y2="16" />
    </svg>
  ),
  AlertTriangle: ({ className }: { className?: string }) => (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z" />
      <line x1="12" y1="9" x2="12" y2="13" />
      <line x1="12" y1="17" x2="12.01" y2="17" />
    </svg>
  ),
  Play: () => (
    <svg viewBox="0 0 24 24" fill="currentColor">
      <path d="M8 5v14l11-7z" />
    </svg>
  ),
  BookmarkPlus: ({ className }: { className?: string }) => (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="m19 21-7-4-7 4V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2v16z" />
      <line x1="12" y1="7" x2="12" y2="13" />
      <line x1="9" y1="10" x2="15" y2="10" />
    </svg>
  ),
  BookmarkCheck: ({ className }: { className?: string }) => (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="m19 21-7-4-7 4V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2v16z" />
      <polyline points="9 10 12 13 16 9" />
    </svg>
  ),
  Frown: ({ className }: { className?: string }) => (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10" />
      <path d="M16 16s-1.5-2-4-2-4 2-4 2" />
      <line x1="9" y1="9" x2="9.01" y2="9" />
      <line x1="15" y1="9" x2="15.01" y2="9" />
    </svg>
  ),
};

interface AudioVariant {
  phonetic: string;
  url: string;
  primary: boolean;
}

interface HeteronymVariant {
  phonetic: string;
  partOfSpeech: string;
  meaning_native: string;
  meaning_en: string;
  context?: string;
  tense?: string;
}

interface HeteronymData {
  word: string;
  variants: HeteronymVariant[];
  note_native?: string;
}

interface WordForms {
  lemma?: string;
  formType?: string;
  past?: string;
  pastParticiple?: string;
  progressive?: string;
  thirdPerson?: string;
  plural?: string;
}

interface DefinitionExample {
  pos: string;              // Part of speech (n., v., adj., etc.)
  meaning_native: string;   // Localized meaning (in current i18n language)
  example_en: string;       // English example sentence
  example_native: string;   // Localized translation of example
}

interface DictionaryEntry {
  word: string;
  phonetic: string | null;
  definition: string | null;
  translation: string | null;
  pos: string | null;
  collins: number;
  oxford: number;
  tag: string | null;
  audio: AudioVariant[] | null;
  found: boolean;
  heteronym?: HeteronymData | null;
  wordForms?: WordForms | null;
  lemmaTranslation?: string | null;
  examples?: DefinitionExample[] | null;
  cefrLevel?: string | null;
  suggestions?: { word: string; translation: string | null }[] | null;
}

type SearchResult = DictionaryEntry;

interface AutocompleteItem {
  word: string;
  translation?: string;
}

function DictionaryPage() {
  const [searchParams] = useSearchParams();
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [playingAudio, setPlayingAudio] = useState<string | null>(null);
  const autocompleteTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const searchWrapperRef = useRef<HTMLDivElement>(null);
  const resultsRef = useRef<HTMLDivElement>(null);
  const initialSearchDoneRef = useRef(false);
  const isSearchingRef = useRef(false);

  const { t } = useTranslation('dictionary');
  // getCurrentLocale() reads i18n.language with fallback; re-evaluated on every render
  // (useTranslation triggers re-render on language change)
  const currentLocale: SupportedLocale = getCurrentLocale();

  // Format POS abbreviation to localized label using shared utility
  const formatPOS = (pos: string): string => {
    return formatPOSUtil(pos, currentLocale);
  };
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [recentSearches, setRecentSearches] = useState<string[]>([]);
  const [autocomplete, setAutocomplete] = useState<AutocompleteItem[]>([]);
  const [showAutocomplete, setShowAutocomplete] = useState(false);
  const [addedWords, setAddedWords] = useState<Set<string>>(new Set());

  // Load recent searches from localStorage
  useEffect(() => {
    try {
      const saved = localStorage.getItem(RECENT_SEARCHES_KEY);
      if (saved) {
        setRecentSearches(JSON.parse(saved));
      }
    } catch (err) {
      console.error('Failed to load recent searches:', err);
    }
  }, []);

  // Handle URL query parameter (?word=xxx) on mount
  useEffect(() => {
    if (initialSearchDoneRef.current) return;

    const wordParam = searchParams.get('word');
    if (wordParam && wordParam.trim()) {
      initialSearchDoneRef.current = true;
      setSearchQuery(wordParam.trim());
      // Trigger search after state is set
      setTimeout(() => {
        handleSearch(undefined, wordParam.trim());
      }, 0);
    }
  }, [searchParams]); // eslint-disable-line react-hooks/exhaustive-deps

  // Click outside to close autocomplete dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchWrapperRef.current && !searchWrapperRef.current.contains(event.target as Node)) {
        setShowAutocomplete(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Save recent searches to localStorage
  const saveRecentSearch = useCallback((word: string) => {
    setRecentSearches((prev) => {
      const filtered = prev.filter((w) => w.toLowerCase() !== word.toLowerCase());
      const updated = [word, ...filtered].slice(0, MAX_RECENT_SEARCHES);
      try {
        localStorage.setItem(RECENT_SEARCHES_KEY, JSON.stringify(updated));
      } catch (err) {
        console.error('Failed to save recent searches:', err);
      }
      return updated;
    });
  }, []);

  const clearRecentSearches = () => {
    setRecentSearches([]);
    try {
      localStorage.removeItem(RECENT_SEARCHES_KEY);
    } catch (err) {
      console.error('Failed to clear recent searches:', err);
    }
  };

  // Play audio pronunciation
  const playAudio = async (url: string) => {
    if (audioRef.current) {
      audioRef.current.pause();
    }

    const audio = new Audio(url);
    audioRef.current = audio;
    setPlayingAudio(url);

    try {
      await audio.play();
    } catch (err) {
      console.error('Failed to play audio:', err);
      setPlayingAudio(null);
    }

    audio.onended = () => {
      setPlayingAudio(null);
    };
  };

  // Cleanup audio on unmount
  useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
  }, []);

  // Autocomplete with debounce
  useEffect(() => {
    if (autocompleteTimeoutRef.current) {
      clearTimeout(autocompleteTimeoutRef.current);
    }

    if (searchQuery.length < 2 || isSearchingRef.current) {
      setAutocomplete([]);
      setShowAutocomplete(false);
      return;
    }

    autocompleteTimeoutRef.current = setTimeout(async () => {
      if (isSearchingRef.current) return;
      try {
        const response = await fetch(
          `${API_BASE_URL}/dictionary/search?q=${encodeURIComponent(searchQuery.trim())}&limit=6&locale=${currentLocale}`
        );
        const data = await response.json();
        if (Array.isArray(data)) {
          setAutocomplete(
            data.map((item: SearchResult) => ({
              word: item.word,
              translation: item.translation?.split('\n')[0] || undefined,
            }))
          );
          setShowAutocomplete(true);
        }
      } catch (err) {
        console.error('Autocomplete error:', err);
      }
    }, 300);

    return () => {
      if (autocompleteTimeoutRef.current) {
        clearTimeout(autocompleteTimeoutRef.current);
      }
    };
  }, [searchQuery, currentLocale]);

  const handleSearch = async (e?: React.FormEvent, overrideQuery?: string) => {
    if (e) e.preventDefault();
    const query = overrideQuery || searchQuery;
    if (!query.trim()) return;

    // Immediately blur and close autocomplete before async work
    isSearchingRef.current = true;
    inputRef.current?.blur();
    setShowAutocomplete(false);
    setAutocomplete([]);
    setLoading(true);
    setError(null);
    setSearchResults([]);

    // Save to recent searches
    saveRecentSearch(query.trim());

    try {
      const url = `${API_BASE_URL}/dictionary/lookup/${encodeURIComponent(query.trim())}?generateAudio=true&locale=${currentLocale}&examples=true`;

      const response = await fetch(url);
      const data = await response.json();

      setSearchResults([data]);

      // If logged in, ask the backend whether this word is already in the user's
      // vocabulary book so the button reflects "已收錄" instead of "加入".
      const token = localStorage.getItem('accessToken');
      if (token && data?.found && data?.word) {
        try {
          const checkUrl = `${API_BASE_URL}/api/v1/vocabulary-book/check?word=${encodeURIComponent(data.word)}`;
          const checkResp = await fetch(checkUrl, {
            headers: { Authorization: `Bearer ${token}` },
          });
          if (checkResp.ok) {
            const checkJson = await checkResp.json();
            if (checkJson?.data?.inBook) {
              setAddedWords((prev) => new Set([...Array.from(prev), data.word]));
            }
          }
        } catch (checkErr) {
          // Non-fatal: fall back to "加入" state
          console.warn('vocab-book check failed:', checkErr);
        }
      }

      // Scroll to results after render
      setTimeout(() => {
        resultsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 50);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unknown error';
      setError(t('searchFailed', { message }));
      console.error('Search error:', err);
    } finally {
      setLoading(false);
      isSearchingRef.current = false;
    }
  };

  const handleAutocompleteSelect = (word: string) => {
    setSearchQuery(word);
    setShowAutocomplete(false);
    handleSearch(undefined, word);
  };

  const handleQuickSearch = (word: string) => {
    isSearchingRef.current = true;
    setSearchQuery(word);
    inputRef.current?.blur();
    setShowAutocomplete(false);
    setAutocomplete([]);
    setTimeout(() => handleSearch(undefined, word), 0);
  };

  const handleAddToVocabulary = async (word: string) => {
    const token = localStorage.getItem('accessToken');
    if (!token) {
      alert(t('pleaseLogin', { defaultValue: 'Please log in first' }));
      return;
    }

    // Find the entry so we can pass phonetic/translation along with the word
    const entry = searchResults.find((e) => e.word === word);

    try {
      const response = await fetch(`${API_BASE_URL}/api/v1/vocabulary-book/word`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          word,
          phonetic: entry?.phonetic || '',
          translation: entry?.translation || '',
        }),
      });

      if (response.ok) {
        setAddedWords((prev) => new Set([...Array.from(prev), word]));
        return;
      }

      // Backend treats duplicates as success (returns existing entry), but be
      // defensive: if we still see an error, surface a friendly message.
      const errData = await response.json().catch(() => ({}));
      alert(errData?.message || t('addToVocabFailed', { defaultValue: 'Failed to add word' }));
    } catch (err) {
      console.error('Failed to add to vocabulary:', err);
      alert(t('addToVocabFailed', { defaultValue: 'Failed to add word' }));
    }
  };

  const parsePartOfSpeech = (pos: string | null): string => {
    if (!pos) return '';

    type PosLocale = SupportedLocale | 'en';
    const posMap: Record<string, Record<PosLocale, string>> = {
      n: { en: 'noun',        'zh-TW': '名詞',   'zh-CN': '名词',   ja: '名詞',   ko: '명사',   vi: 'danh từ',    th: 'คำนาม',          ms: 'kata nama',   id: 'kata benda' },
      v: { en: 'verb',        'zh-TW': '動詞',   'zh-CN': '动词',   ja: '動詞',   ko: '동사',   vi: 'động từ',    th: 'คำกริยา',        ms: 'kata kerja',  id: 'kata kerja' },
      j: { en: 'adjective',   'zh-TW': '形容詞', 'zh-CN': '形容词', ja: '形容詞', ko: '형용사', vi: 'tính từ',     th: 'คำคุณศัพท์',    ms: 'kata sifat',  id: 'kata sifat' },
      r: { en: 'adverb',      'zh-TW': '副詞',   'zh-CN': '副词',   ja: '副詞',   ko: '부사',   vi: 'trạng từ',   th: 'คำวิเศษณ์',     ms: 'kata keterangan', id: 'kata keterangan' },
      p: { en: 'preposition', 'zh-TW': '介詞',   'zh-CN': '介词',   ja: '前置詞', ko: '전치사', vi: 'giới từ',     th: 'คำบุพบท',       ms: 'kata sendi',  id: 'kata depan' },
      c: { en: 'conjunction', 'zh-TW': '連詞',   'zh-CN': '连词',   ja: '接続詞', ko: '접속사', vi: 'liên từ',    th: 'คำสันธาน',      ms: 'kata hubung', id: 'kata penghubung' },
      u: { en: 'auxiliary',   'zh-TW': '助詞',   'zh-CN': '助词',   ja: '助動詞', ko: '조동사', vi: 'trợ động từ', th: 'กริยาช่วย',     ms: 'kata kerja bantu', id: 'kata kerja bantu' },
      i: { en: 'interjection','zh-TW': '感嘆詞', 'zh-CN': '感叹词', ja: '感動詞', ko: '감탄사', vi: 'thán từ',    th: 'คำอุทาน',       ms: 'kata seru',   id: 'kata seru' },
      d: { en: 'determiner',  'zh-TW': '限定詞', 'zh-CN': '限定词', ja: '限定詞', ko: '한정사', vi: 'từ hạn định', th: 'คำกำหนด',       ms: 'kata penentu', id: 'kata penentu' },
      m: { en: 'number',      'zh-TW': '數詞',   'zh-CN': '数词',   ja: '数詞',   ko: '수사',   vi: 'số từ',      th: 'คำเลข',          ms: 'kata bilangan', id: 'kata bilangan' },
      x: { en: 'other',       'zh-TW': '其他',   'zh-CN': '其他',   ja: 'その他', ko: '기타',   vi: 'khác',       th: 'อื่นๆ',          ms: 'lain-lain',   id: 'lain-lain' },
    };

    const locale: PosLocale = (currentLocale in posMap['n']) ? currentLocale : 'en';
    const sep = ['zh-TW', 'zh-CN', 'ja'].includes(currentLocale) ? '、' : ', ';

    const parts = pos.split('/').map((part) => {
      const match = part.match(/([a-z]):(\d+)/);
      if (match) {
        const [, code, freq] = match;
        const entry = posMap[code];
        if (entry) {
          return `${entry[locale]}(${freq}%)`;
        }
      }
      return part;
    });

    return parts.join(sep);
  };

  const renderPhonetics = (entry: DictionaryEntry) => {
    if (!entry.phonetic) return null;

    if (!entry.audio || entry.audio.length === 0) {
      return <span className="phonetic">/{entry.phonetic}/</span>;
    }

    const phonetics = entry.phonetic.split(' / ');

    if (phonetics.length > 1 && entry.audio.length > 1) {
      return (
        <div className="phonetics-container">
          {phonetics.map((phonetic, index) => {
            const audioVariant = entry.audio?.[index];
            if (!audioVariant) return null;

            const isPlaying = playingAudio === audioVariant.url;

            return (
              <span key={index} className="phonetic-item">
                <button
                  className={`audio-button ${isPlaying ? 'playing' : ''}`}
                  onClick={() => playAudio(audioVariant.url)}
                  title={t('playPronunciation')}
                  aria-label={`${t('playPronunciation')} /${phonetic.trim()}/`}
                >
                  <Icons.Play />
                </button>
                <span className="phonetic">/{phonetic.trim()}/</span>
              </span>
            );
          })}
        </div>
      );
    }

    const audioVariant = entry.audio[0];
    const isPlaying = playingAudio === audioVariant.url;

    return (
      <span className="phonetic-item">
        <button
          className={`audio-button ${isPlaying ? 'playing' : ''}`}
          onClick={() => playAudio(audioVariant.url)}
          title={t('playPronunciation')}
          aria-label={`${t('playPronunciation')} /${entry.phonetic}/`}
        >
          <Icons.Play />
        </button>
        <span className="phonetic">/{entry.phonetic}/</span>
      </span>
    );
  };

  const renderDifficultyBadges = (entry: DictionaryEntry) => {
    const badges = [];

    if (entry.cefrLevel) {
      badges.push(
        <span key="cefr" className={`badge badge-cefr badge-cefr-${entry.cefrLevel.toLowerCase()}`}>
          CEFR {entry.cefrLevel}
        </span>
      );
    }

    if (entry.collins && entry.collins > 0) {
      badges.push(
        <span key="collins" className="badge badge-collins">
          Collins {'★'.repeat(entry.collins)}
        </span>
      );
    }

    if (entry.oxford && entry.oxford > 0) {
      badges.push(
        <span key="oxford" className="badge badge-oxford">
          Oxford
        </span>
      );
    }

    if (entry.tag) {
      const tags = entry.tag.split(' ').filter((tag) => tag.trim());
      tags.forEach((tag, idx) => {
        badges.push(
          <span key={`tag-${idx}`} className="badge badge-tag">
            {tag.toUpperCase()}
          </span>
        );
      });
    }

    return badges;
  };

  // Skeleton Loading Component
  const SkeletonCard = () => (
    <div className="skeleton-card" aria-hidden="true">
      <div className="skeleton-header">
        <div className="skeleton-word" />
        <div className="skeleton-phonetic" />
      </div>
      <div className="skeleton-badges">
        <div className="skeleton-badge" />
        <div className="skeleton-badge" />
      </div>
      <div className="skeleton-text" />
      <div className="skeleton-text medium" />
      <div className="skeleton-text short" />
    </div>
  );

  return (
    <div className="dictionary-page">
      <div className="dictionary-container">
        <header className="dictionary-header">
          <h1>
            <Icons.Book />
            {t('title')}
          </h1>
        </header>

        <div className="search-section">
          <form onSubmit={handleSearch} className="search-form">
            {/* Search Input Group with Label */}
            <div className="search-input-group">
              <div className="search-input-container">
                <div className="search-input-wrapper" ref={searchWrapperRef}>
                  <input
                    ref={inputRef}
                    id="dictionary-search"
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onFocus={() => !isSearchingRef.current && autocomplete.length > 0 && setShowAutocomplete(true)}
                    onBlur={() => setTimeout(() => setShowAutocomplete(false), 200)}
                    placeholder={t('inputPlaceholder.lookup')}
                    className="search-input"
                    autoComplete="off"
                    aria-autocomplete="list"
                    role="combobox"
                    aria-controls="autocomplete-list"
                    aria-expanded={showAutocomplete}
                  />
                  {searchQuery && (
                    <button
                      type="button"
                      className="search-input-clear"
                      onClick={() => {
                        setSearchQuery('');
                        inputRef.current?.focus();
                      }}
                      aria-label={t('clearSearchLabel')}
                    >
                      <Icons.X className="icon" />
                    </button>
                  )}
                  {/* Autocomplete Dropdown */}
                  {showAutocomplete && !loading && autocomplete.length > 0 && (
                    <div id="autocomplete-list" className="autocomplete-dropdown" role="listbox">
                      {autocomplete.map((item, index) => (
                        <button
                          key={index}
                          type="button"
                          role="option"
                          aria-selected={false}
                          className="autocomplete-item"
                          onClick={() => handleAutocompleteSelect(item.word)}
                        >
                          <span className="autocomplete-word">{item.word}</span>
                          {item.translation && <span className="autocomplete-hint">{item.translation}</span>}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                <button type="submit" className="search-button" disabled={loading}>
                  {loading ? (
                    <>
                      <span className="spinner" />
                      {t('searching')}
                    </>
                  ) : (
                    <>
                      <Icons.Search className="button-icon" />
                      {t('searchButton')}
                    </>
                  )}
                </button>
              </div>
            </div>

            {/* Recent Searches */}
            {recentSearches.length > 0 && (
              <div className="recent-searches">
                <div className="recent-searches-header">
                  <span className="recent-searches-title">
                    <Icons.Clock className="history-icon" />
                    {t('recentSearches')}
                  </span>
                  <button type="button" className="recent-searches-clear" onClick={clearRecentSearches}>
                    {t('clearHistory')}
                  </button>
                </div>
                <div className="recent-searches-list">
                  {recentSearches.map((word, index) => (
                    <button key={index} type="button" className="recent-search-btn" onClick={() => handleQuickSearch(word)}>
                      {word}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </form>
        </div>

        {/* Error Message */}
        {error && (
          <div className="error-message" role="alert">
            <Icons.AlertCircle className="error-icon" />
            {error}
          </div>
        )}

        {/* Loading Skeleton */}
        {loading && (
          <div className="results-section">
            <h2 className="results-header">{t('resultsHeader')}</h2>
            <SkeletonCard />
          </div>
        )}

        {/* Search Results */}
        {!loading && searchResults.length > 0 && (
          <div className="results-section" ref={resultsRef}>
            <h2 className="results-header">
              {t('resultsHeader')} ({searchResults.length})
            </h2>

            {searchResults.map((entry, index) => (
              <div key={index} className={`result-card ${!entry.found ? 'not-found' : ''}`}>
                {!entry.found ? (
                  <div className="not-found-message">
                    <Icons.Frown className="not-found-icon" />
                    <h3>
                      {t('notFound')}：{entry.word}
                    </h3>
                    <p>{t('notFoundDesc')}</p>
                    {entry.suggestions && entry.suggestions.length > 0 && (
                      <div className="did-you-mean">
                        <p className="did-you-mean-label">{t('didYouMean')}</p>
                        <div className="did-you-mean-list">
                          {entry.suggestions.map((s) => (
                            <button
                              key={s.word}
                              type="button"
                              className="did-you-mean-chip"
                              onClick={() => handleQuickSearch(s.word)}
                            >
                              <span className="dym-word">{s.word}</span>
                              {s.translation && (
                                <span className="dym-trans">{s.translation.split('\n')[0]}</span>
                              )}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <>
                    <div className="result-header">
                      <h3 className="word">{entry.word}</h3>
                      {renderPhonetics(entry)}
                      <button
                        className={`btn-add-vocab ${addedWords.has(entry.word) ? 'added' : ''}`}
                        onClick={() => handleAddToVocabulary(entry.word)}
                        disabled={addedWords.has(entry.word)}
                        aria-label={addedWords.has(entry.word) ? t('addedToVocab') : t('addToVocab')}
                      >
                        {addedWords.has(entry.word) ? (
                          <>
                            <Icons.BookmarkCheck className="vocab-icon" />
                            {t('addedToVocab')}
                          </>
                        ) : (
                          <>
                            <Icons.BookmarkPlus className="vocab-icon" />
                            {t('addToVocab')}
                          </>
                        )}
                      </button>
                    </div>

                    {entry.pos && (
                      <div className="pos">
                        <strong>{t('partOfSpeech')}：</strong>
                        {parsePartOfSpeech(entry.pos)}
                      </div>
                    )}

                    <div className="badges">{renderDifficultyBadges(entry)}</div>

                    {/* Heteronym Alert */}
                    {entry.heteronym && entry.heteronym.variants.length > 1 && (
                      <div className="heteronym-alert">
                        <div className="heteronym-header">
                          <Icons.AlertTriangle className="alert-icon" />
                          <span className="heteronym-title">{t('heteronymTitle')}</span>
                          <span className="heteronym-subtitle">{t('heteronymSubtitle')}</span>
                        </div>
                        <div className="heteronym-variants">
                          {entry.heteronym.variants.map((variant, idx) => (
                            <div key={idx} className="heteronym-variant">
                              <div className="variant-header">
                                <span className="variant-phonetic">/{variant.phonetic}/</span>
                                <span className="variant-pos">{variant.partOfSpeech}</span>
                                {variant.tense && (
                                  <span className="variant-tense">
                                    ({variant.tense === 'present' ? t('tensePresent') : variant.tense === 'past' ? t('tensePast') : variant.tense})
                                  </span>
                                )}
                              </div>
                              <div className="variant-meaning">
                                <span className="meaning-zh">{variant.meaning_native}</span>
                              </div>
                              {variant.context && (
                                <div className="variant-context">
                                  <span className="context-label">{t('exampleLabel')}</span>
                                  <span className="context-example">{variant.context}</span>
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                        {entry.heteronym.note_native && (
                          <div className="heteronym-note">
                            <Icons.Lightbulb className="note-icon" />
                            {entry.heteronym.note_native}
                          </div>
                        )}
                      </div>
                    )}

                    {entry.translation && (
                      <div className="translation">
                        <strong>{t('translation')}：</strong>
                        <div className="translation-content">
                          {entry.translation.split('\n').map((line, idx) => {
                            // Strip "word: " prefix (Gemini translation artifact)
                            const cleanLine = line.replace(/^\w[\w-]*:\s*/, '');
                            // Parse POS prefix (e.g., "vt. 擠出；榨出", "v.phr. 突然大哭起來")
                            const posMatch = cleanLine.match(/^([a-z]+\.(?:phr\.)?)\s+(.+)$/i);
                            if (posMatch) {
                              return (
                                <p key={idx}>
                                  <span className="pos-label">{formatPOS(posMatch[1])}</span> {posMatch[2]}
                                </p>
                              );
                            }
                            return cleanLine.trim() ? <p key={idx}>{cleanLine}</p> : null;
                          })}
                          {entry.wordForms?.lemma && (
                            <div className="lemma-section">
                              <div className="lemma-header">
                                <span className="lemma-arrow">{entry.word} →{' '}</span>
                                <span
                                  className="lemma-link"
                                  onClick={() => handleQuickSearch(entry.wordForms!.lemma!)}
                                >
                                  {entry.wordForms.lemma}
                                </span>
                                {entry.wordForms.formType && (
                                  <span className="form-type-badge">
                                    {t(`formType.${entry.wordForms.formType}`, { defaultValue: entry.wordForms.formType })}
                                  </span>
                                )}
                              </div>
                              {entry.lemmaTranslation && (
                                <div className="lemma-translation">
                                  {entry.lemmaTranslation.split('\n').map((line: string, idx: number) => {
                                    const cleanLine = line.replace(/^\w[\w-]*:\s*/, '');
                                    const posMatch = cleanLine.match(/^([a-z]+\.(?:phr\.)?)\s+(.+)$/i);
                                    if (posMatch) {
                                      return (
                                        <p key={`lemma-${idx}`}>
                                          <span className="pos-label">{formatPOS(posMatch[1])}</span> {posMatch[2]}
                                        </p>
                                      );
                                    }
                                    return cleanLine.trim() ? <p key={`lemma-${idx}`}>{cleanLine}</p> : null;
                                  })}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Examples Section */}
                    {entry.examples && entry.examples.length > 0 && (
                      <div className="examples-section">
                        <strong>{t('examplesTitle')}：</strong>
                        <div className="examples-list">
                          {entry.examples.map((example, idx) => (
                            <div key={idx} className="example-item">
                              <div className="example-header">
                                <span className="example-pos">{formatPOS(example.pos)}</span>
                                <span className="example-meaning">{example.meaning_native}</span>
                              </div>
                              <div className="example-en">
                                <ClickableText text={example.example_en} />
                              </div>
                              <div className="example-zh">{example.example_native}</div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            ))}
          </div>
        )}

      </div>
    </div>
  );
}

export default DictionaryPage;
